import { IChainTokenStat } from 'app-lib/apis/bzxApi'
import { Asset } from 'app-lib/assetUtils'
import numberUtils from 'app-lib/numberUtils'
import appConfig, {
  chainConfigs,
  ChainId,
  routes,
  supportedChains,
} from 'bzx-common/src/config/appConfig'
import routeConfig from 'bzx-common/src/config/routeConfig'
import { RootStore, StatsStore, UIStore } from 'bzx-common/src/stores'
import { createChart, IChartApi, ISeriesApi } from 'lightweight-charts'
import * as mobx from 'mobx'
import React from 'react'
import web3Utils from 'web3-utils'
import { config, themeConfig } from './Reserve/StatsChart/StatsChartConfig'

type ChartValue = 'tvlUsd' | 'tvl' | 'apr'

export interface IStatsVM {
  selectedChain: ChainId | null
  numberPagination: number
  searchUser: string
  selectedAsset: Asset | null
  secondAsset: Asset | null
  selectedData: ChartValue
  allActivity: boolean
}

export default class StatsVM implements IStatsVM {
  rootStore: RootStore
  statsStore: StatsStore
  uiStore: UIStore
  selectedChain: ChainId | null = null
  numberPagination = 0
  paginationSize = 5
  searchUser = ''
  selectedAsset: Asset | null = null
  secondAsset: Asset | null = null
  selectedData: ChartValue = 'tvlUsd'
  allActivity = false
  stopSyncTheme?: mobx.IReactionDisposer
  stopSyncChartData?: mobx.IReactionDisposer
  chartContainer?: React.RefObject<HTMLDivElement>
  chart?: IChartApi
  areaSeries?: ISeriesApi<'Area'>
  secondAreaSeries?: ISeriesApi<'Area'>

  get isLastEventsPagination() {
    return (
      this.numberPagination + 1 === this.quantityGrids ||
      this.latestProtocolEvents.length <= this.statsStore.defaultQuantityEvents
    )
  }

  get latestProtocolEvents() {
    const { latestProtocolEvents, selectedUserEvents } = this.statsStore
    if (selectedUserEvents.value.length) {
      return selectedUserEvents.value.filter(
        (event) => this.allActivity || event.chainId === this.selectedChain
      )
    }

    return latestProtocolEvents.value.filter(
      (event) =>
        (this.allActivity || event.chainId === this.selectedChain) &&
        (!this.searchUser || event.user.includes(this.searchUser))
    )
  }

  get dataLabel() {
    if (this.selectedData === 'tvl') {
      return 'Locked as Collateral'
    }
    if (this.selectedData === 'apr') {
      return 'Lending Rates'
    }
    return 'TVL (USD)'
  }

  get latestActivity() {
    const startIndex = this.statsStore.defaultQuantityEvents * this.numberPagination
    const endIndex =
      this.statsStore.defaultQuantityEvents * this.numberPagination +
      this.statsStore.defaultQuantityEvents

    return this.latestProtocolEvents.slice(startIndex, endIndex)
  }

  get selectedChainStats(): IChainTokenStat[] {
    const stats = this.statsStore.stats.value.find((stat) => {
      return stat.chainId === this.selectedChain
    })
    return stats ? stats.stats : []
  }

  get selectedChainAllTokensStats() {
    const allTokensStats = this.statsStore.stats.value.find(
      (stat) => stat.chainId === this.selectedChain
    )?.allTokensStats
    const usdSupply = allTokensStats?.usdSupply || 'NaN'
    const usdTotalLocked = allTokensStats?.usdTotalLocked || 'NaN'
    return { usdSupply, usdTotalLocked }
  }

  get quantityGrids() {
    return Math.ceil(this.latestProtocolEvents.length / this.statsStore.defaultQuantityEvents)
  }

  get paginations() {
    let startIndex = this.numberPagination - Math.floor(this.paginationSize / 2)

    if (startIndex < 1) {
      startIndex = 0
    } else if (startIndex + this.paginationSize > this.quantityGrids) {
      startIndex = this.quantityGrids - this.paginationSize
    }

    const endIndex = startIndex + this.paginationSize

    return [...Array(this.quantityGrids)].map((x, i) => i).slice(startIndex, endIndex)
  }

  get reserveChain() {
    const { chainId } = this.rootStore.web3Connection
    return this.selectedChain ? this.selectedChain : chainId
  }

  get chartStatus() {
    const { assetStatsData, tvlData } = this.statsStore
    const data = this.selectedAsset ? assetStatsData : tvlData
    const empty = !assetStatsData.value.length && !tvlData.value.length
    return { pending: data.pending, error: data.error?.toString(), empty }
  }

  get assets() {
    return (
      this.reserveChain && [Asset.UNKNOWN].concat(chainConfigs[this.reserveChain].lending.assets)
    )
  }

  get firstAssets() {
    return this.assets?.filter((asset) => asset !== this.secondAsset)
  }

  get secondAssets() {
    return this.assets?.filter((asset) => asset !== this.selectedAsset)
  }

  get iconName() {
    return (this.reserveChain && chainConfigs[this.reserveChain].icon.name) || ''
  }

  get native() {
    if (!this.selectedChain) {
      return null
    }
    return chainConfigs[this.selectedChain].native
  }

  get chartData() {
    // get info for wrapped tokens
    const selectedAsset =
      this.selectedAsset === this.native!.asset ? this.native!.wrapped : this.selectedAsset
    const points = selectedAsset
      ? this.statsStore.assetStatsData.value[selectedAsset.toLowerCase()]
      : this.statsStore.tvlData.value
    if (!points) {
      return []
    }
    return points.reduce((result, point, index) => {
      const prevTime =
        points[index - 1] && new Date(points[index - 1].timestamp).toISOString().split('T')[0]
      const time = new Date(point.timestamp).toISOString().split('T')[0]
      time !== prevTime && result.push({ time, value: point[this.selectedData] })

      return result
    }, [] as Array<{ time: string; value?: number }>)
  }

  get secondChartData() {
    // get info for wrapped tokens
    const secondAsset =
      this.secondAsset === this.native!.asset ? this.native!.wrapped : this.secondAsset
    if (!secondAsset) {
      return []
    }
    const points = this.statsStore.assetStatsData.value[secondAsset.toLowerCase()]

    if (!points) {
      return []
    }
    return points.map((point) => {
      const time = new Date(point.timestamp).toISOString().split('T')[0]
      return { time, value: point[this.selectedData] }
    })
  }

  get selectedLine() {
    const { theme } = this.uiStore
    const config = themeConfig[theme]
    return config.series.lineColor
  }

  get secondLine() {
    const { theme } = this.uiStore
    const config = themeConfig[theme]
    return config.secondSeries.lineColor
  }

  showAllActivity() {
    this.set('allActivity', !this.allActivity)
    supportedChains.map((chainId) => {
      this.statsStore.latestProtocolEvents.fetch(chainId)
    })
  }

  syncToTheme() {
    const { theme } = this.uiStore
    const config = themeConfig[theme]
    const container = this.chartContainer?.current

    if (this.chart) {
      this.chart.applyOptions(config.chart)
      this.chart.applyOptions({
        height: container?.offsetHeight,
        width: container?.offsetWidth,
      })
    }
    this.areaSeries?.applyOptions(config.series)
    this.secondAreaSeries?.applyOptions(config.secondSeries)
  }

  nextPagination = () => {
    !this.isLastEventsPagination && this.set('numberPagination', this.numberPagination + 1)
  }

  prevPagination = () => {
    this.numberPagination !== 0 && this.set('numberPagination', this.numberPagination - 1)
  }

  getDefaults() {
    this.assign({ selectedAsset: null, secondAsset: null, selectedData: 'tvlUsd' })
    this.areaSeries?.setData([])
    this.secondAreaSeries?.setData([])
  }

  /**
   * Refresh stats (total borrow, lent, aprs, etc) for the current chain onchain (not API)
   */
  updateChain(selectedChain: ChainId) {
    const { web3Connection, route } = this.rootStore

    this.assign({ numberPagination: 0, selectedChain })

    if (route === routes.reserve) {
      !this.selectedChainStats.length &&
        this.statsStore.stats.fetch(selectedChain).then(() => {
          if (!this.selectedChainStats.length) {
            // switch network to get data onchain
            web3Connection.switchNetwork(selectedChain)
          }
        })
      this.loadCharData(selectedChain)
    } else {
      this.statsStore.latestProtocolEvents.fetch(selectedChain)
    }
  }

  setSecondChartData() {
    this.secondAreaSeries?.setData(this.secondChartData)
  }

  setChartData() {
    this.areaSeries?.setData(this.chartData)
    this.chart &&
      this.chart.applyOptions({
        localization: {
          priceFormatter: (p: number) => `${numberUtils.abbreviate(p)}`,
        },
      })
  }

  loadCharData(chainId: ChainId, newData = true) {
    const { assetStatsData, tvlData } = this.statsStore
    if (newData) {
      tvlData.fetch(chainId).then(() => {
        this.getDefaults()
        this.setChartData()
        // expand chart to the container
        this.chart?.timeScale().fitContent()
      })
      assetStatsData.fetch(chainId).then(() => this.setChartData())
    } else {
      this.setChartData()
    }
  }

  findByUser(searchUser: string) {
    this.statsStore.selectedUserEvents.reset()
    this.assign({ searchUser, numberPagination: 0 })
    web3Utils.isAddress(searchUser) && this.statsStore.selectedUserEvents.fetch(searchUser)
  }

  onDataSwitch(selectedData: ChartValue) {
    this.assign({ selectedData })
    this.setChartData()
    this.setSecondChartData()
  }

  onAssetSwitch(selectedAsset: Asset) {
    if (selectedAsset === Asset.UNKNOWN) {
      this.getDefaults()
    } else {
      this.assign({ selectedAsset })
    }
    this.setChartData()
  }

  onSecondAssetSwitch(secondAsset: Asset) {
    if (secondAsset === Asset.UNKNOWN) {
      this.assign({ secondAsset: null })
    } else {
      this.assign({ secondAsset })
    }
    this.setSecondChartData()
  }

  /**
   * Helper to set values through mobx actions.
   */

  set(prop: keyof IStatsVM, value: any) {
    ;(this[prop] as any) = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  assign(props: { [key: string]: any }) {
    Object.assign(this, props)
  }

  init() {
    this.chartContainer = React.createRef()
    const { chainId } = this.rootStore.web3Connection
    chainId && this.updateChain(chainId)
    // delay to get chart container
    setTimeout(() => {
      if (this.chartContainer?.current) {
        this.chart = createChart(this.chartContainer.current, config)
        this.areaSeries = this.chart.addAreaSeries({
          lineWidth: 2,
        })
        this.secondAreaSeries = this.chart.addAreaSeries({
          lineWidth: 2,
        })
        this.syncToTheme()
      }
      const { tvlData, latestProtocolEvents } = this.statsStore
      chainId && this.loadCharData(chainId, !tvlData.loaded)
      latestProtocolEvents.fetch(chainId)
    }, appConfig.delay)

    this.stopSyncTheme = mobx.reaction(
      () => {
        const { theme } = this.uiStore
        const { width, height } = this.uiStore.media.viewPort.size
        return {
          theme,
          width,
          height,
        }
      },
      () => this.syncToTheme()
    )

    this.stopSyncChartData = mobx.reaction(
      () => {
        const { route, web3Connection } = this.rootStore
        const { chainId } = web3Connection

        return { chainId, route }
      },
      (data) => {
        if (data.chainId) {
          this.updateChain(data.chainId)
        }
      }
    )

    !this.statsStore.tvlData.loaded && this.statsStore.tvlData.fetch(chainId)
    !this.statsStore.assetStatsData.loaded && this.statsStore.assetStatsData.fetch(chainId)

    if (this.rootStore.route.endsWith(routeConfig.treasury)) {
      this.statsStore.treasuryBalances.fetch()
    }
  }

  destroyVM() {
    this.stopSyncTheme && this.stopSyncTheme()
    this.stopSyncChartData && this.stopSyncChartData()
    this.chart && this.chart.remove()
  }

  constructor({ rootStore }: { rootStore: RootStore }) {
    this.rootStore = rootStore
    this.statsStore = rootStore.statsStore
    this.uiStore = rootStore.uiStore
    mobx.makeAutoObservable(this, undefined, { deep: false, autoBind: true })
    this.init()
  }
}
